import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Hook personalizado para gestionar los datos y la lógica de la página de Empresas
 */
export const useCompaniesData = (currentUser) => {
  // Data state
  const [companies, setCompanies] = useState([]);
  const [typsaOrgUnits, setTypsaOrgUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Search
  const [search, setSearch] = useState('');
  
  // TYPSA filters
  const [typsaGroupByOwner, setTypsaGroupByOwner] = useState(false);
  
  // Publica filters
  const [publicaStatusFilter, setPublicaStatusFilter] = useState('all');
  const [publicaRolFilter, setPublicaRolFilter] = useState('all');
  const [publicaSortBy, setPublicaSortBy] = useState('owner');
  
  // Privada filters
  const [privadaStatusFilter, setPrivadaStatusFilter] = useState('all');
  const [privadaRolFilter, setPrivadaRolFilter] = useState('all');
  const [privadaSortBy, setPrivadaSortBy] = useState('owner');
  
  // Expansion states
  const [expandedTypes, setExpandedTypes] = useState({ typsa: false, publica: false, privada: false });
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedOwnerGroups, setExpandedOwnerGroups] = useState({});

  // Fetch data
  const fetchCompanies = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/companies`);
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTypsaOrgUnits = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/typsa-org-units`);
      setTypsaOrgUnits(response.data);
    } catch (error) {
      console.error('Error fetching TYPSA org units:', error);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
    fetchTypsaOrgUnits();
  }, [fetchCompanies, fetchTypsaOrgUnits]);

  // Delete TYPSA unit
  const handleDeleteTypsaUnit = useCallback(async (unit) => {
    if (!window.confirm(`¿Eliminar "${unit.value}"?`)) return;
    try {
      await axios.delete(`${API}/typsa-org-units/${unit.id}`, {
        headers: { 'x-user-email': currentUser?.email }
      });
      toast.success('Unidad eliminada');
      fetchTypsaOrgUnits();
    } catch (error) {
      console.error('Error deleting unit:', error);
      toast.error('Error al eliminar');
    }
  }, [currentUser?.email, fetchTypsaOrgUnits]);

  // Toggle functions
  const toggleType = useCallback((type) => {
    setExpandedTypes(prev => ({ ...prev, [type]: !prev[type] }));
  }, []);

  const toggleGroup = useCallback((key) => {
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const toggleOwnerGroup = useCallback((key) => {
    setExpandedOwnerGroups(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // Filter by search
  const filterBySearch = useCallback((items, isTypsa = false) => {
    if (!search) return items;
    const searchLower = search.toLowerCase();
    
    if (isTypsa) {
      return items.filter(unit => 
        unit.value?.toLowerCase().includes(searchLower) ||
        unit.owner?.toLowerCase().includes(searchLower) ||
        unit.description?.toLowerCase().includes(searchLower)
      );
    }
    
    return items.filter(company =>
      company.name?.toLowerCase().includes(searchLower) ||
      company.sector?.toLowerCase().includes(searchLower) ||
      company.owner?.toLowerCase().includes(searchLower)
    );
  }, [search]);

  // Group companies by sort criteria
  const groupCompaniesBySort = useCallback((companiesList, sortBy) => {
    let sorted = [...companiesList];
    
    if (sortBy === 'name') {
      sorted.sort((a, b) => {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB, 'es');
      });
      
      if (sorted.length <= 6) {
        if (sorted.length === 0) return [];
        const firstLetter = (sorted[0].name || '#')[0].toUpperCase();
        const lastLetter = (sorted[sorted.length - 1].name || '#')[0].toUpperCase();
        const label = firstLetter === lastLetter ? firstLetter : `${firstLetter} - ${lastLetter}`;
        return [{ key: 'all', label, companies: sorted }];
      }
      
      const targetGroupSize = Math.max(4, Math.ceil(sorted.length / 4));
      const groups = [];
      let currentGroup = [];
      let groupStartLetter = '';
      
      sorted.forEach((company, index) => {
        const letter = (company.name || '#')[0].toUpperCase();
        
        if (currentGroup.length === 0) {
          groupStartLetter = letter;
        }
        
        currentGroup.push(company);
        
        const isLastCompany = index === sorted.length - 1;
        const nextLetter = !isLastCompany ? (sorted[index + 1].name || '#')[0].toUpperCase() : null;
        const groupIsFull = currentGroup.length >= targetGroupSize;
        const letterChanging = nextLetter && nextLetter !== letter;
        
        if (isLastCompany || (groupIsFull && letterChanging)) {
          const groupEndLetter = letter;
          const label = groupStartLetter === groupEndLetter 
            ? groupStartLetter 
            : `${groupStartLetter} - ${groupEndLetter}`;
          
          groups.push({
            key: `${groupStartLetter}-${groupEndLetter}`,
            label,
            companies: [...currentGroup]
          });
          currentGroup = [];
        }
      });
      
      return groups;
      
    } else if (sortBy === 'owner') {
      sorted.sort((a, b) => {
        const ownerA = (a.owner || 'Sin responsable').toLowerCase();
        const ownerB = (b.owner || 'Sin responsable').toLowerCase();
        return ownerA.localeCompare(ownerB, 'es');
      });
      
      const groups = {};
      sorted.forEach(company => {
        const owner = company.owner || 'Sin responsable';
        if (!groups[owner]) groups[owner] = [];
        groups[owner].push(company);
      });
      
      const sortedKeys = Object.keys(groups).sort((a, b) => a.localeCompare(b, 'es'));
      return sortedKeys.map(key => ({ key, label: key, companies: groups[key] }));
    }
    
    return [{ key: 'all', label: '', companies: sorted }];
  }, []);

  // Group TYPSA units by owner
  const groupTypsaUnitsByOwner = useCallback((units) => {
    const groups = {};
    units.forEach(unit => {
      const owner = unit.owner || 'Sin responsable';
      if (!groups[owner]) groups[owner] = [];
      groups[owner].push(unit);
    });
    
    const sortedOwners = Object.keys(groups).sort((a, b) => a.localeCompare(b, 'es'));
    return sortedOwners.map(owner => ({
      owner,
      units: groups[owner].sort((a, b) => (a.value || '').localeCompare(b.value || '', 'es'))
    }));
  }, []);

  // Sort TYPSA units alphabetically
  const sortTypsaUnitsAlphabetically = useCallback((units) => {
    return [...units].sort((a, b) => (a.value || '').localeCompare(b.value || '', 'es'));
  }, []);

  // Computed: TYPSA categories
  const typsaCategories = useMemo(() => {
    const categories = [
      { key: 'division', label: 'Divisiones' },
      { key: 'departamento', label: 'Departamentos' },
      { key: 'territorio', label: 'Territorios' },
      { key: 'grupo', label: 'Grupo' }
    ];
    
    const filteredUnits = filterBySearch(typsaOrgUnits, true);
    
    return categories.map(cat => {
      const normalizedCatKey = cat.key.toLowerCase().trim();
      const unitsInCategory = filteredUnits.filter(u => 
        u.category && u.category.toLowerCase().trim() === normalizedCatKey
      );
      
      if (typsaGroupByOwner) {
        return {
          ...cat,
          groupedByOwner: true,
          ownerGroups: groupTypsaUnitsByOwner(unitsInCategory),
          totalUnits: unitsInCategory.length
        };
      } else {
        return {
          ...cat,
          groupedByOwner: false,
          units: sortTypsaUnitsAlphabetically(unitsInCategory),
          totalUnits: unitsInCategory.length
        };
      }
    }).filter(cat => cat.totalUnits > 0);
  }, [typsaOrgUnits, typsaGroupByOwner, filterBySearch, groupTypsaUnitsByOwner, sortTypsaUnitsAlphabetically]);

  // Computed: Companies by type
  const companiesByType = useMemo(() => {
    const types = [
      { 
        key: 'publica', 
        label: 'Públicas', 
        color: 'bg-green-500',
        statusFilter: publicaStatusFilter,
        rolFilter: publicaRolFilter,
        sortBy: publicaSortBy
      },
      { 
        key: 'privada', 
        label: 'Privadas', 
        color: 'bg-purple-500',
        statusFilter: privadaStatusFilter,
        rolFilter: privadaRolFilter,
        sortBy: privadaSortBy
      }
    ];
    
    return types.map(type => {
      let companiesOfType = companies.filter(c => {
        if (c.company_type === 'typsa' || c.name?.toUpperCase().includes('TYPSA')) {
          return false;
        }
        if (!c.company_type) {
          return type.key === 'privada';
        }
        return c.company_type === type.key;
      });
      
      companiesOfType = filterBySearch(companiesOfType);
      
      if (type.statusFilter !== 'all') {
        companiesOfType = companiesOfType.filter(c => c.status === type.statusFilter);
      }
      
      if (type.rolFilter !== 'all') {
        companiesOfType = companiesOfType.filter(c => c.rol === type.rolFilter);
      }
      
      return {
        ...type,
        companies: companiesOfType,
        groups: groupCompaniesBySort(companiesOfType, type.sortBy)
      };
    });
  }, [companies, publicaStatusFilter, publicaRolFilter, publicaSortBy, privadaStatusFilter, privadaRolFilter, privadaSortBy, filterBySearch, groupCompaniesBySort]);

  // Computed: Total counts
  const totalTypsaUnits = typsaOrgUnits.length;
  const filteredTypsaCount = typsaCategories.reduce((acc, cat) => acc + cat.totalUnits, 0);
  const totalNonTypsaCompanies = companies.filter(c => c.company_type !== 'typsa').length;

  return {
    // Data
    companies,
    typsaOrgUnits,
    loading,
    
    // Search
    search,
    setSearch,
    
    // TYPSA filters
    typsaGroupByOwner,
    setTypsaGroupByOwner,
    
    // Publica filters
    publicaStatusFilter,
    setPublicaStatusFilter,
    publicaRolFilter,
    setPublicaRolFilter,
    publicaSortBy,
    setPublicaSortBy,
    
    // Privada filters
    privadaStatusFilter,
    setPrivadaStatusFilter,
    privadaRolFilter,
    setPrivadaRolFilter,
    privadaSortBy,
    setPrivadaSortBy,
    
    // Expansion states
    expandedTypes,
    expandedGroups,
    expandedOwnerGroups,
    
    // Toggle functions
    toggleType,
    toggleGroup,
    toggleOwnerGroup,
    
    // Actions
    fetchCompanies,
    fetchTypsaOrgUnits,
    handleDeleteTypsaUnit,
    
    // Computed data
    typsaCategories,
    companiesByType,
    totalTypsaUnits,
    filteredTypsaCount,
    totalNonTypsaCompanies,
  };
};

export default useCompaniesData;
